// JSON-tree snapshot of the rendered #page DOM for the sidechain agent.
//
// Replaces the legacy HTML snapshot. Two wins over HTML:
//   1. Smaller payload — keys + values only, no markup/whitespace overhead.
//   2. Microdiff-friendly — getContextDiffs can emit deltas for the structural
//      changes that happen between turns, instead of re-sending the full snapshot.
//
// Node shape:
//   { tag, id?, class?, data?, children?, text? }
//
// `data` keys have the `data-` prefix stripped (e.g. data-page-item-id="abc"
// becomes { 'page-item-id': 'abc' }). `text` is set only when an element has
// a single text child; mixed content goes into `children` with text nodes
// represented as { text: '...' }.
//
// Filters applied:
//   - Vue scoped-style hashes (data-v-XXXXXXXX) are dropped.
//   - data-builder-ui is dropped, and any subtree marked with data-builder-ui
//     is excluded entirely (builder overlays the agent must not see).
//   - data-builder-ui-root is dropped but its subtree is kept — the marker
//     opts the element itself out of user CSS without hiding the section's
//     user content from the agent.
//   - <script>, <style>, and <svg> are emitted as stubs (tag only) — their
//     bodies are large or render-internal, but keeping the element preserves
//     :nth-child indexing for any siblings the agent targets.

const STUB_TAGS = new Set(['script', 'style', 'svg']);

const collectDataAttrs = (el) => {
  const out = {};
  for (const attr of el.attributes) {
    const { name } = attr;
    if (!name.startsWith('data-')) continue;
    if (name === 'data-builder-ui') continue;
    if (name === 'data-builder-ui-root') continue;
    if (name.startsWith('data-v-')) continue;
    out[name.slice(5)] = attr.value;
  }
  return out;
};

const serializeElement = (el) => {
  const tag = el.tagName.toLowerCase();
  const node = { tag };

  if (el.id) {
    node.id = el.id;
  }

  if (typeof el.className === 'string' && el.className.trim()) {
    node.class = el.className.trim();
  }

  const data = collectDataAttrs(el);
  if (Object.keys(data).length > 0) {
    node.data = data;
  }

  if (STUB_TAGS.has(tag)) {
    return node;
  }

  const children = [];
  let pendingText = '';
  let sawElement = false;

  for (const child of el.childNodes) {
    if (child.nodeType === 3) {
      const text = child.textContent.trim();
      if (!text) continue;
      if (sawElement) {
        children.push({ text });
      } else {
        pendingText = pendingText ? `${pendingText} ${text}` : text;
      }
      continue;
    }
    if (child.nodeType !== 1) continue;
    if (child.hasAttribute('data-builder-ui')) continue;

    if (pendingText && !sawElement) {
      children.push({ text: pendingText });
      pendingText = '';
    }
    sawElement = true;
    children.push(serializeElement(child));
  }

  if (sawElement) {
    node.children = children;
  } else if (pendingText) {
    node.text = pendingText;
  }

  return node;
};

export default function captureTemplateJson() {
  const pageEl = document.getElementById('page');
  if (!pageEl) return undefined;
  return serializeElement(pageEl);
}
